import * as THREE from 'three';
import { PALETTE } from './palette';

const tmpColor = new THREE.Color();

/** Warna seragam per-vertex (untuk flat shading tanpa tekstur). */
export function tint(geo: THREE.BufferGeometry, hex: number): THREE.BufferGeometry {
  const g = geo.index === null ? geo : geo.toNonIndexed();
  const n = g.attributes.position.count;
  const c = new THREE.Color(hex);
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = c.r;
    arr[i * 3 + 1] = c.g;
    arr[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  g.computeVertexNormals();
  return g;
}

/** Dinding kotak akrilik arena + langit gradien pastel + prisma ramp.
 *  Semua visual murni; collider dinding dibangun di ThreeSceneManager
 *  (box tipis), bukan di sini. */

/** Prisma ramp: muka ATAS tepat di permukaan jalan (lowY..highY sepanjang Z),
 *  solid setebal t di bawahnya. Non-indexed, siap di-tint. */
export function makeRampPrism(
  w: number,
  d: number,
  lowY: number,
  highY: number,
  t = 0.5,
): THREE.BufferGeometry {
  const hw = w / 2;
  const hd = d / 2;
  const pts: Array<[number, number, number]> = [
    [-hw, lowY, -hd], //  0 FL top front
    [hw, lowY, -hd], //   1 FR
    [-hw, highY, hd], //  2 BL top back
    [hw, highY, hd], //   3 BR
    [-hw, lowY - t, -hd], // 4 flap bottom front
    [hw, lowY - t, -hd], //  5
    [-hw, highY - t, hd], // 6
    [hw, highY - t, hd], //  7
  ];
  const faces: Array<[number, number, number]> = [
    // TOP (normal ke atas): (BL,FR,FL),(BL,BR,FR)
    [2, 1, 0],
    [2, 3, 1],
    // BOTTOM: (4,5,6),(6,5,7)
    [4, 5, 6],
    [6, 5, 7],
    // LEFT: (0,4,6),(6,2,0)
    [0, 4, 6],
    [6, 2, 0],
    // RIGHT: (5,1,3),(7,5,3)
    [5, 1, 3],
    [7, 5, 3],
    // FRONT: (0,1,4),(4,1,5)
    [0, 1, 4],
    [4, 1, 5],
    // BACK: (6,7,2),(7,3,2)
    [6, 7, 2],
    [7, 3, 2],
  ];
  const pos: number[] = [];
  for (const [a, b, c] of faces) {
    pos.push(...pts[a], ...pts[b], ...pts[c]);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  return geo;
}

/** Panel dinding "paper-art" low-poly: langit + bukit + awan + matahari
 *  (siluet datar, tanpa tekstur) — sesuai gaya kotak diorama.
 *  Tiap lapisan diberi offset z NYATA (2-8cm): mencegah z-fighting antar
 *  lapisan saat kamera bergerak (blink) di jarak arena. */
function makeArtPanel(width: number, height: number): THREE.Group {
  const g = new THREE.Group();
  const paint = (hex: number): THREE.MeshBasicMaterial =>
    new THREE.MeshBasicMaterial({ color: hex, side: THREE.DoubleSide });

  const bg = new THREE.Mesh(new THREE.PlaneGeometry(width, height), paint(0xb7d9ea));
  g.add(bg);

  // Lembar bukit JAUH (zigzag lembut, sage pucat) — z = +0.02.
  const far = new THREE.Mesh(
    zigzagBand(width, height * 0.06, height * 0.22, 5),
    paint(0xc5ddbc),
  );
  far.position.z = 0.02;
  g.add(far);
  // Lembar bukit DEKAT (zigzag lebih tinggi, sage) — z = +0.04.
  const near = new THREE.Mesh(
    zigzagBand(width, -height * 0.5, height * 0.62, 4),
    paint(0x9cc39f),
  );
  near.position.z = 0.04;
  g.add(near);

  // Puff awan (3 gumpalan elips tumpuk) — z = +0.06.
  for (const [cx, cy, s] of [
    [-width * 0.28, height * 0.34, 1.15],
    [width * 0.1, height * 0.42, 0.9],
    [width * 0.38, height * 0.28, 0.75],
  ] as Array<[number, number, number]>) {
    const puff = makeCloudPuff(s);
    puff.position.set(cx, cy, 0.06);
    g.add(puff);
  }

  // Matahari (cakram pastel, pojok kiri atas) — z = +0.08.
  const sun = new THREE.Mesh(new THREE.CircleGeometry(Math.min(width, height) * 0.07, 18), paint(0xf5d98a));
  sun.position.set(-width * 0.38, height * 0.4, 0.08);
  g.add(sun);

  return g;
}

/** Gumpalan awan: 2-3 cakram elips bertumpuk (warna putih lembut). */
function makeCloudPuff(scale: number): THREE.Group {
  const g = new THREE.Group();
  const white = new THREE.MeshBasicMaterial({ color: 0xf6f5f2, side: THREE.DoubleSide });
  const r = 0.34 * scale;
  const parts: Array<[number, number, number]> = [
    [0, 0, r],
    [0.62 * r, 0.16 * r, 0.62 * r],
    [-0.6 * r, 0.12 * r, 0.5 * r],
  ];
  for (const [x, y, rad] of parts) {
    const c = new THREE.Mesh(new THREE.CircleGeometry(rad, 10), white);
    c.scale.set(1.15, 0.62, 1);
    c.position.set(x, y, 0.001);
    g.add(c);
  }
  return g;
}

/** Lembar bukit rendah-poly: strip segitiga dengan pegunungan di tepi atas. */
function zigzagBand(width: number, dropY: number, bandH: number, peaks: number): THREE.BufferGeometry {
  const n = peaks * 2 + 1;
  const pos: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const x0 = -width / 2 + (width * i) / (n - 1);
    const x1 = -width / 2 + (width * (i + 1)) / (n - 1);
    const y0 = i % 2 === 0 ? dropY + bandH : dropY + bandH * 0.42;
    const y1 = (i + 1) % 2 === 0 ? dropY + bandH : dropY + bandH * 0.42;
    pos.push(
      x0, y0, 0,
      x1, y1, 0,
      x0, dropY, 0,
      x0, dropY, 0,
      x1, y1, 0,
      x1, dropY, 0,
    );
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  geo.computeVertexNormals();
  return geo;
}

/** Dinding kotak akrilik: panel ART 2D pastel di sisi dalam + tepi bingkai
 *  krem terang. Solid (tidak tembus pandang), tanpa tekstur foto. */
export function buildArenaBox(half: number, height = 9): THREE.Group {
  const group = new THREE.Group();
  group.name = 'ArenaBox';

  const frame = new THREE.MeshStandardMaterial({
    color: PALETTE.frameCream,
    emissive: 0xfff1cf,
    emissiveIntensity: 0.22,
    roughness: 0.6,
    flatShading: true,
  });

  const panel = makeArtPanel(half * 2, height);
  const panels: Array<[number, number]> = [
    [0, -half],
    [0, half],
    [-half, 0],
    [half, 0],
  ];
  for (let i = 0; i < panels.length; i++) {
    const [x, z] = panels[i];
    const art = panel.clone(true);
    art.position.set(x, height / 2, z);
    if (i >= 2) art.rotation.y = Math.PI / 2;
    group.add(art);
  }

  const TH = 0.16;
  const pillarGeo = new THREE.BoxGeometry(TH, height, TH);
  const corners: Array<[number, number]> = [
    [-half, -half],
    [half, -half],
    [-half, half],
    [half, half],
  ];
  for (const [x, z] of corners) {
    const p = new THREE.Mesh(pillarGeo, frame);
    p.position.set(x, height / 2, z);
    p.castShadow = true;
    group.add(p);
  }

  const rimGeo = new THREE.BoxGeometry(half * 2, TH, TH);
  const rims: Array<[number, number, number, number]> = [
    [0, height + TH / 2, -half, 0],
    [0, height + TH / 2, half, 0],
    [-half, height + TH / 2, 0, Math.PI / 2],
    [half, height + TH / 2, 0, Math.PI / 2],
  ];
  for (const [x, y, z, ry] of rims) {
    const r = new THREE.Mesh(rimGeo, frame);
    r.position.set(x, y, z);
    r.rotation.y = ry;
    r.castShadow = true;
    group.add(r);
  }

  return group;
}

/** Langit gradien pastel (bola belakang, faset 16x8): biru muda -> krem.
 *  fog=false supaya jarak jauh tidak memucatkan gradien. */
export function buildSkyDome(radius = 48): THREE.Mesh {
  const geo = new THREE.SphereGeometry(radius, 16, 8);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const n = pos.count;
  const top = new THREE.Color(PALETTE.skyTop);
  const horizon = new THREE.Color(PALETTE.skyHorizon);
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const t = THREE.MathUtils.clamp(pos.getY(i) / radius, 0, 1);
    const c = tmpColor.copy(horizon).lerp(top, t);
    arr[i * 3] = c.r;
    arr[i * 3 + 1] = c.g;
    arr[i * 3 + 2] = c.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  // Non-indexed supaya setiap segitiga satu faset (gradien patah-patah,
  // bukan smooth) - sesuai gaya low-poly.
  const g = geo.toNonIndexed();
  const mat = new THREE.MeshBasicMaterial({
    vertexColors: true,
    side: THREE.BackSide,
    fog: false,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(g, mat);
  mesh.name = 'SkyDome';
  mesh.renderOrder = -10;
  return mesh;
}